import { useEffect, useState } from 'react';
import { discoverScmToken } from '@/api/endpoints';
import type { ScmTokenDiscoverResult } from '@/api/schemas';
import type { ScmValues } from './scmConnectionModel';
import { discoveryChoices, isChosen, parseBitbucketUrl, sourceValues, type ScmSource, type SourceChoice } from './sourceModel';

const UNSUPPORTED: ScmTokenDiscoverResult = { outcome: 'unsupported', repositories: [], more: false, message: null };
type Apply = (s: { workspace: string; repository: string }) => void;

/** Asks the API what the pasted token reaches (debounced, once per token); a failed call falls back to the URL. */
function useDiscovery(token: string) {
  const [answer, setAnswer] = useState<{ token: string; result: ScmTokenDiscoverResult } | null>(null);
  useEffect(() => {
    if (token.length < 20) return;
    let live = true;
    const timer = setTimeout(() => {
      discoverScmToken(token)
        .catch(() => UNSUPPORTED)
        .then((result) => live && setAnswer({ token, result }));
    }, 400);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [token]);
  return answer?.token === token ? answer.result : null;
}

/** The only choice is picked for the person; a choice that no longer fits the kind is left for them to change. */
function useAutoPick(choices: SourceChoice[], values: ScmValues, apply: Apply) {
  const only = choices.length === 1 ? choices[0] : undefined;
  useEffect(() => {
    if (only && !isChosen(only, values)) apply(sourceValues(values.kind, only));
    // Re-run when the single choice or the kind changes, not on every keystroke elsewhere in the form.
  }, [only?.key, values.kind]);
}

/**
 * Where the token points, without typing slugs (plan P34): the repositories or workspaces discovery found (the only one is
 * picked), or, when Bitbucket does not list them, the workspace and repository read from a pasted URL.
 */
export function useDiscoverScmToken(values: ScmValues, apply: Apply) {
  const token = values.token.trim();
  const result = useDiscovery(token);
  const [url, setUrl] = useState('');
  const choices = result?.outcome === 'listed' ? discoveryChoices(values.kind, result.repositories) : [];
  useAutoPick(choices, values, apply);
  const parsed: ScmSource | null = url.trim() ? parseBitbucketUrl(url) : null;
  const onUrl = (text: string) => {
    setUrl(text);
    const s = parseBitbucketUrl(text);
    apply(s ? sourceValues(values.kind, s) : { workspace: '', repository: '' });
  };
  return {
    searching: token.length >= 20 && result === null,
    result,
    choices,
    needsUrl: result !== null && result.outcome !== 'rejected' && choices.length === 0,
    url,
    parsed,
    onUrl,
    pick: (c: SourceChoice) => apply(sourceValues(values.kind, c)),
  };
}

export type DiscoverScmToken = ReturnType<typeof useDiscoverScmToken>;
